import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { analyticsApi, AnalyticsOverview, TimelinePoint, CampaignPerformance, CurrentUsage, RecentCall } from '../../api/analytics';

// ─── Utility ────────────────────────────────────────────────────────────────

function fmtDuration(secs: number | null): string {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtPhone(p: string): string {
  return p || '—';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function usageMeterColor(pct: number): string {
  if (pct >= 95) return '#ef4444'; // red
  if (pct >= 80) return '#f97316'; // orange
  if (pct >= 60) return '#eab308'; // yellow
  return '#10b981'; // green
}

function statusColor(status: string): string {
  if (status === 'completed') return '#34d399';
  if (status === 'failed') return '#f87171';
  return '#9ca3af';
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: React.ReactNode;
  icon: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, icon }) => (
  <div
    className="rounded-xl p-5 flex flex-col gap-2"
    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">{label}</span>
      <span className="text-xl">{icon}</span>
    </div>
    <div className="text-3xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-gray-400">{sub}</div>}
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

export const AnalyticsDashboard: React.FC = () => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignPerformance[]>([]);
  const [usage, setUsage] = useState<CurrentUsage | null>(null);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        setError(null);
        const [ovRes, usRes, campRes, recRes] = await Promise.all([
          analyticsApi.getOverview(),
          analyticsApi.getCurrentUsage(),
          analyticsApi.getCampaignPerformance(),
          analyticsApi.getRecentCalls(),
        ]);
        setOverview(ovRes.data.data);
        setUsage(usRes.data.data);
        setCampaigns(campRes.data.data);
        setRecentCalls(recRes.data.data);
      } catch (e: any) {
        setError('Failed to load analytics. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    analyticsApi.getTimeline(period).then(res => setTimeline(res.data.data)).catch(() => {});
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading analytics…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  const usagePct = usage?.percentUsed ?? 0;
  const meterColor = usageMeterColor(usagePct);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Page title */}
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-gray-400 text-sm mt-1">Usage, call performance, and campaign insights</p>
      </div>

      {/* ── Top stat cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Calls This Month"
          value={overview?.callsThisMonth ?? 0}
          icon="📞"
          sub={
            overview?.callsMonthChange !== null && overview?.callsMonthChange !== undefined ? (
              <span style={{ color: (overview.callsMonthChange ?? 0) >= 0 ? '#34d399' : '#f87171' }}>
                {(overview.callsMonthChange ?? 0) >= 0 ? '▲' : '▼'} {Math.abs(overview.callsMonthChange ?? 0)}% vs last month
              </span>
            ) : 'No data last month'
          }
        />
        <StatCard
          label="Answer Rate"
          value={`${overview?.answerRate ?? 0}%`}
          icon="✅"
          sub={<span className="text-gray-400">Calls completed / initiated</span>}
        />
        <StatCard
          label="Minutes Used"
          value={`${overview?.minutesUsed ?? 0} / ${overview?.minutesTotal ?? 0}`}
          icon="⏱️"
          sub={
            <div className="w-full mt-1">
              <div className="w-full bg-gray-700 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (overview?.minutesUsed ?? 0) / (overview?.minutesTotal ?? 1) * 100)}%`, background: meterColor }}
                />
              </div>
            </div>
          }
        />
        <StatCard
          label="Active Campaigns"
          value={overview?.activeCampaigns ?? 0}
          icon="📢"
          sub={`${overview?.totalAgents ?? 0} agents total`}
        />
      </div>

      {/* ── Usage Meter ─────────────────────────────────────────────── */}
      {usage && (
        <div
          className="rounded-xl p-6"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-white font-semibold text-base">Usage Meter</h2>
              <p className="text-gray-400 text-sm mt-0.5">
                {usage.minutesUsed} of {usage.minutesTotal} minutes used · {usage.daysLeftInPeriod} days left in billing period
                {usage.projectedOverage > 0 && (
                  <span className="text-orange-400 ml-2">· Projected overage: {usage.projectedOverage} min</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className="text-2xl font-bold"
                style={{ color: meterColor }}
              >
                {usage.percentUsed}%
              </span>
              {usagePct >= 80 && (
                <Link
                  to="/billing"
                  className="px-3 py-1.5 rounded-lg text-sm font-medium no-underline"
                  style={{ background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#818cf8' }}
                >
                  Upgrade Plan
                </Link>
              )}
            </div>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
            <div
              className="h-4 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, usagePct)}%`, background: meterColor }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>0 min</span>
            <span>{usage.minutesTotal} min ({usage.plan})</span>
          </div>
        </div>
      )}

      {/* ── Charts row ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calls over time */}
        <div
          className="lg:col-span-2 rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-semibold">Calls Over Time</h2>
            <div className="flex gap-1">
              {(['7d', '30d', '90d'] as const).map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className="px-2.5 py-1 rounded text-xs font-medium transition-all"
                  style={{
                    background: period === p ? 'rgba(99,102,241,0.2)' : 'transparent',
                    color: period === p ? '#818cf8' : '#6b7280',
                    border: period === p ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={timeline}>
              <defs>
                <linearGradient id="callGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ background: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e5e7eb' }}
                labelStyle={{ color: '#9ca3af' }}
              />
              <Area type="monotone" dataKey="calls" stroke="#6366f1" fill="url(#callGrad)" strokeWidth={2} dot={false} name="Total Calls" />
              <Area type="monotone" dataKey="answered" stroke="#10b981" fill="none" strokeWidth={2} dot={false} name="Answered" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Campaign performance */}
        <div
          className="rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <h2 className="text-white font-semibold mb-4">Campaign Performance</h2>
          {campaigns.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-500 text-sm">No campaigns yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={campaigns.slice(0, 5)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} width={80} />
                <Tooltip
                  contentStyle={{ background: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e5e7eb' }}
                />
                <Bar dataKey="totalCalls" fill="#6366f1" radius={[0, 4, 4, 0]} name="Total Calls" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Recent Calls Table ──────────────────────────────────────── */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h2 className="text-white font-semibold">Recent Calls</h2>
          <Link to="/call-logs" className="text-indigo-400 text-sm hover:text-indigo-300 no-underline">
            View all →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Phone', 'Agent', 'Duration', 'Status', 'Outcome', 'Time'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentCalls.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500">No calls yet</td>
                </tr>
              ) : (
                recentCalls.map((call) => (
                  <tr key={call.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td className="px-5 py-3 text-gray-300 font-mono text-xs">{fmtPhone(call.phone)}</td>
                    <td className="px-5 py-3 text-gray-300">{call.agent}</td>
                    <td className="px-5 py-3 text-gray-400">{fmtDuration(call.duration)}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${statusColor(call.status)}20`, color: statusColor(call.status) }}>
                        {call.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-400">{call.outcome}</td>
                    <td className="px-5 py-3 text-gray-500 text-xs">{fmtDate(call.timestamp)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
